
















































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import CategoryHeader from '@/components/CategoryHeader.vue';
import OptionEdit from '@/components/OptionEdit.vue';
import {CurrencyFilter} from '@/filters/currency';
import {MenuStore} from '@/store/modules/menu.store';
import { isEqual, isObject, transform } from 'lodash';
import {WebService} from '@/services/web.service';
import {OverlayShowTime} from '@/constants';
import {Helpers} from '@/services/helpers.service';
import AddExistingOption from '@/components/AddExistingOption.vue';

export interface IEditItem {
	itemId: string | null;
	name: string;
	description: string;
	price: number;
	minChoices: number;
	maxChoices: number;
	options: IEditOption[];
}

export interface IEditOption {
	optionId: string | null;
	name: string;
	priority: number;
	maxChoices: number;
	minChoices: number;
	choices: IEditChoice[];
}

export interface IEditChoice {
	choiceId: string | null;
	name: string;
	price: number;
	selected: boolean;
}




@Component({
	components: {
		'category-header': CategoryHeader,
		'option-edit': OptionEdit,
		'add-existing-option': AddExistingOption
	}
})
export default class ItemEdit extends Vue {
	@Prop({required: true})
	menuId!: string;

	@Prop({required: true})
	categoryId!: number;

	@Prop({required: true})
	itemId!: string | null;

	itemName = '';
	itemPrice = '';
	itemDescription = '';
	options: IEditOption[] = [];

	showOverlay = false;
	showLoading = false;

	showAddExistingOption = false;

	loadingOverlays: {[key: string]: boolean} = {
		name: false,
		price: false,
		description: false
	};

	private ignoreChanges = false;


	get item() {
		if(this.itemId === null) {
			return null;
		}
		return MenuStore.itemByMenu(this.menuId, this.itemId);
	}

	get isDirty() {
		// Until we save this initially then we need to assume dirty
		if(this.itemId === null) {
			return true;
		}
		if(this.item === null) {
			return false;
		}
		return !isEqual(this.cleanServerItem, this.cleanItem);
	}

	get cleanServerItem() {
		if(this.item === null) {
			return null;
		}
		return {
			itemId: this.item.itemId,
			name: this.item.name.trim(),
			price: this.item.price,
			description: this.item.description.trim()
		};
	}

	get cleanItem() {
		if(this.item === null) {
			return null;
		}
		return {
			itemId: this.item.itemId,
			name: this.itemName.trim(),
			price: this.cleanItemPrice,
			description: this.itemDescription.trim()
		};
	}

	get cleanItemPrice() {
		return Number(this.itemPrice.replace(/[^0-9]/g, ''));
	}

	get canSave() {
		return this.itemName.trim() !== '';
	}

	get hasUnsavedOptions() {
		return this.options.reduce((bool, option) => {
			if(option.optionId === null) {
				return true;
			}
			return bool;
		}, false);
	}

	created() {
		if(this.itemId === null) {
			this.addOption();
		}
		// if(this.badge !== null) {
		// 	this.badgeName = this.badge.name;
		// } else {
		// 	this.close();
		// }
	}

	@Watch('itemId', {immediate: true})
	@Watch('item', {deep: true})
	setupItem() {
		if(this.item) {
			this.ignoreChanges = true;
			console.log('IGNORING CHANGES START');
			// Something with that .lazy modifier makes this get overridden if I don't
			// just delay till the next tick
			this.$nextTick(() => {
				if(this.item) {
					this.itemName = this.item.name;
					this.itemPrice = CurrencyFilter(this.item.price);
					this.itemDescription = this.item.description;
					this.options = this.item.optionIds.map((optionId) => {
						const option = MenuStore.optionByMenu(this.menuId, optionId);
						if(!option) {
							throw new Error('Missing optionId');
						}
						return {
							optionId: optionId,
							name: option.name,
							priority: option.priority,
							maxChoices: option.maxChoices,
							minChoices: option.minChoices,
							choices: Object.values(option.choices).map((choice) => {
								return {
									choiceId: choice.choiceId,
									name: choice.name,
									price: choice.price,
									selected: choice.selected,
								};
							})
						};
					}).sort((a, b) => {
						if(a.priority === b.priority) {
							return 0;
						}
						return a.priority > b.priority ? -1 : 1;
					});
					this.ignoreChanges = false;
					console.log('IGNORING CHANGES END');
				}
			});
		}
	}

	close() {
		this.showOverlay = false;
	}

	finalClose() {
		this.$emit('close');
	}

	async saveItem() {
		if(
			!this.ignoreChanges &&
			this.isDirty &&
			this.canSave
		) {
			console.log('Saving Item', this.cleanItem, this.cleanServerItem);
			if(this.itemId === null) {
				await this.createItem();
			} else {
				const diff = Helpers.diff(this.cleanItem, this.cleanServerItem);
				const fieldsBeingUpdated = [];
				for(const key in diff) {
					if(key in this.loadingOverlays) {
						fieldsBeingUpdated.push(key);
					}
				}

				await this.updateItem(fieldsBeingUpdated);

				// alert('Not Implemented');
			}
		}
	}

	async createItem() {
		let errorMessage = 'There was a problem creating your item';
		try {
			this.loadingOverlays.name = true;
			// this.showLoading = true;
			const itemId = await WebService.createItem({
				name: this.itemName,
				price: this.cleanItemPrice,
				description: this.itemDescription,
				// categoryId: this.categoryId
			});
			errorMessage = 'There was a problem adding your item to the menu';
			await WebService.addItemToMenu(this.menuId, this.categoryId, itemId);
			// this.showLoading = false;
			this.loadingOverlays.name = false;
			this.$emit('update:itemId', itemId);
		} catch(e) {
			this.loadingOverlays.name = false;
			// this.showLoading = false;
			await this.$bvModal.msgBoxOk(errorMessage, {
				title: 'Error',
				centered: true,
				okTitle: 'Ok'
			});
		}
	}

	async updateItem(fieldsBeingUpdated: string[]) {
		if(this.itemId !== null) {
			try {
				fieldsBeingUpdated.forEach((key) => {
					this.loadingOverlays[key] = true;
				});
				await WebService.updateItem(this.menuId, {
					itemId: this.itemId,
					name: this.itemName,
					price: this.cleanItemPrice,
					description: this.itemDescription,
				});
				fieldsBeingUpdated.forEach((key) => {
					this.loadingOverlays[key] = false;
				});
				// this.$emit('update:itemId', itemId);
			} catch(e) {
				fieldsBeingUpdated.forEach((key) => {
					this.loadingOverlays[key] = false;
				});
				await this.$bvModal.msgBoxOk('There was a problem updating your item', {
					title: 'Error',
					centered: true,
					okTitle: 'Ok'
				});
			}
		}
	}

	addOption() {
		// We are starting at 100 for the first option and going down from there
		// It's not an ideal situation but options are sorted in desc priority order
		// So this is the solution so that new items show up at the bottom and also so that
		// we (probably) won't hit negative numbers.
		this.options.push({
			optionId: null,
			name: '',
			priority: this.options.reduce((min, option) => {
				if(option.priority < min) {
					min = option.priority;
				}
				return min;
			}, 101) - 1,
			maxChoices: 0,
			minChoices: 0,
			choices: [],
		});
	}

	addExistingOption() {
		this.showAddExistingOption = true;
	}

	deleteOption(index: number) {
		this.options.splice(index, 1);
	}

	async deleteItem() {
		if(this.itemId === null) {
			this.close();
		} else {
			if(await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this item?', {
				centered: true,
				okTitle: 'Delete Item'
			})) {
				try {
					this.showLoading = true;
					await WebService.removeItemFromMenu(this.menuId, this.categoryId, this.itemId);
					this.showLoading = false;
					this.close();
				} catch(e) {
					this.showLoading = false;
					await this.$bvModal.msgBoxOk('There was a problem deleting your item', {
						title: 'Error',
						centered: true,
						okTitle: 'Ok'
					});
				}
			}
		}
	}

	// Commented out in the UI right now until we figure out how/if we want this to work
	duplicateItem() {
		alert('Not Implemented');
	}


	moveUp(index: number) {
		const moving = this.options[index];
		const swap = this.options[index - 1];
		const tempPriority = swap.priority;
		swap.priority = moving.priority;
		moving.priority = tempPriority;
		this.options.splice(index - 1, 2, moving, swap);
		console.log('Move Up', moving, swap);
		this.$nextTick(() => {
			// I want to re-work this to be all backed by vuex but for now we will just reach in and force a save
			(this.$refs.options as OptionEdit[])[index - 1].updateOption(['position']);
			(this.$refs.options as OptionEdit[])[index].updateOption(['position']);
		});
	}

	moveDown(index: number) {
		const moving = this.options[index];
		const swap = this.options[index + 1];
		const tempPriority = swap.priority;
		swap.priority = moving.priority;
		moving.priority = tempPriority;
		this.options.splice(index, 2, swap, moving);
		console.log('Move Down', moving, swap);
		this.$nextTick(() => {
			// I want to re-work this to be all backed by vuex but for now we will just reach in and force a save
			(this.$refs.options as OptionEdit[])[index].updateOption(['position']);
			(this.$refs.options as OptionEdit[])[index + 1].updateOption(['position']);
		});
	}

}
